import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { FaBoxOpen, FaCloudUploadAlt, FaPlus, FaSearch } from "react-icons/fa";
import SummaryApi from "../common";
import AdminProductCard from "../components/AdminProductCard";
import UploadProduct from "../components/UploadProduct";
import { request } from "../helpers/request";

import Pagination from "../components/Pagination";

const AllProducts = () => {
  const [openUploadProduct, setOpenUploadProduct] = useState(false);
  const [allProduct, setAllProduct] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchAllProduct = async (currentPage = 1, searchTerm = "") => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: 10,
        search: searchTerm,
      });
      const response = await request(
        `${SummaryApi.allProduct.url}?${queryParams.toString()}`,
        {
          method: SummaryApi.allProduct.method,
        },
      );
      const dataResponse = await response.json();

      if (dataResponse.success) {
        setAllProduct(dataResponse?.data || []);
        setTotalPages(
          dataResponse?.pages || dataResponse?.pagination?.totalPages || 1,
        );
        setPage(
          dataResponse?.page || dataResponse?.pagination?.currentPage || 1,
        );
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchAllProduct(1, search);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchAllProduct(newPage, search);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8 md:mb-12">
        <div className="space-y-1.5 md:space-y-2">
          <div className="flex items-center gap-2 md:gap-3 text-accent text-[10px] md:text-xs font-black uppercase tracking-[0.2em] mb-1">
            <FaBoxOpen size={10} className="md:size-[12px]" />
            <span>Inventory Control</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-primary tracking-tight">
            Catalogue
          </h1>
          <p className="text-[11px] md:text-sm font-medium text-gray-500">
            Manage your premium collection ({allProduct.length} items on this
            page)
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4 flex-1 max-w-2xl">
          <div className="relative flex-1 group">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-accent transition-colors" />
            <input
              type="text"
              placeholder="Search products, brands..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-11 md:h-12 pl-12 pr-4 bg-white border border-surface-border rounded-2xl outline-none focus:border-accent/30 focus:ring-4 focus:ring-accent/5 transition-all text-sm font-medium"
            />
          </div>

          <button
            onClick={() => setOpenUploadProduct(true)}
            className="inline-flex items-center justify-center gap-3 px-6 md:px-8 h-11 md:h-14 bg-primary text-white font-black rounded-2xl hover:bg-accent transition-all shadow-xl shadow-primary/20 active:scale-95 group"
          >
            <div className="w-5 h-5 md:w-6 md:h-6 bg-white/20 rounded-full flex items-center justify-center group-hover:rotate-90 transition-transform duration-500">
              <FaPlus size={10} />
            </div>
            <span className="whitespace-nowrap text-sm md:text-base">
              New Product
            </span>
          </button>
        </div>
      </div>

      {/* Product Grid */}
      <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
              <div
                key={i}
                className="bg-white rounded-[1.5rem] md:rounded-[2rem] border border-surface-border overflow-hidden animate-pulse flex flex-col"
              >
                <div className="h-40 md:h-48 bg-surface-muted" />
                <div className="p-4 md:p-6 space-y-3">
                  <div className="flex justify-between items-center">
                    <div className="h-3 bg-surface-muted rounded-full w-1/4" />
                    <div className="h-3 bg-surface-muted rounded-full w-1/4" />
                  </div>
                  <div className="h-5 bg-surface-muted rounded-full w-full" />
                  <div className="h-5 bg-surface-muted rounded-full w-2/3" />
                  <div className="pt-2 space-y-2">
                    <div className="h-3 bg-surface-muted rounded-full w-1/4" />
                    <div className="h-5 bg-surface-muted rounded-full w-1/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : allProduct.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6 lg:gap-8 pb-12">
              {allProduct.map((product, index) => (
                <motion.div
                  key={index + "allProduct"}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <AdminProductCard
                    data={product}
                    fetchdata={() => fetchAllProduct(page, search)}
                  />
                </motion.div>
              ))}
            </div>
            <Pagination
              page={page}
              pages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        ) : (
          <div className="h-[50vh] flex flex-col items-center justify-center bg-white rounded-[2.5rem] border border-surface-border text-center p-12 shadow-premium">
            <div className="w-20 h-20 bg-surface-muted rounded-full flex items-center justify-center mb-6">
              <FaCloudUploadAlt className="text-gray-300 text-3xl" />
            </div>
            <h3 className="text-2xl font-black text-primary mb-2">
              {search ? "No results found" : "No Products Found"}
            </h3>
            <p className="text-gray-500 mb-8 max-w-xs">
              {search
                ? `We couldn't find anything matching "${search}"`
                : "Your catalogue is currently empty. Start by uploading your first premium helmet."}
            </p>
            {!search && (
              <button
                onClick={() => setOpenUploadProduct(true)}
                className="px-10 py-4 bg-primary text-white font-bold rounded-full hover:bg-accent transition-all shadow-lg"
              >
                Upload Now
              </button>
            )}
            {search && (
              <button
                onClick={() => setSearch("")}
                className="px-10 py-4 bg-surface-muted text-primary font-bold rounded-full hover:bg-surface-border transition-all"
              >
                Clear Search
              </button>
            )}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {openUploadProduct && (
        <UploadProduct
          onClose={() => setOpenUploadProduct(false)}
          fetchData={() => fetchAllProduct(page, search)}
        />
      )}
    </div>
  );
};

export default AllProducts;
